import { severityTextColor, SEVERITY_LABELS } from "./markers";
import type { SiteSeverity } from "@safers/api";

export interface PopoverSite {
  id: string;
  label: string;
  severity: SiteSeverity;
}

export interface PopoverState {
  x: number;
  y: number;
  sites: PopoverSite[];
  clusterKey?: string;
}

interface Props {
  state: PopoverState;
  containerWidth: number;
  containerHeight: number;
  onSitePick: (id: string) => void;
}

/** 선택 목록 바탕. 브랜드 주황은 안에 든 등급 색(주의·경고)과 겹쳐 위험으로 읽힌다. */
const BG = "var(--info-strong)";
const CONNECTOR = 32;
const RADIUS = 8;

const LIST_W = 160;
const ROW_H = 28;
const LIST_PAD_Y = 6;

const EDGE = 4;

function clampTop(top: number, height: number, containerHeight: number) {
  return Math.min(Math.max(top, EDGE), Math.max(containerHeight - height - EDGE, EDGE));
}

export function MapPopover({ state, containerWidth, containerHeight, onSitePick }: Props) {
  const width = LIST_W;
  // 겹친 현장이 많으면 지도 높이까지만 펼치고 목록을 스크롤한다.
  const height = Math.min(
    LIST_PAD_Y * 2 + state.sites.length * ROW_H,
    Math.max(containerHeight - EDGE * 2, ROW_H)
  );

  const toLeft = state.x > containerWidth / 2;
  const left = toLeft ? state.x - CONNECTOR - width : state.x + CONNECTOR;
  const top = clampTop(state.y - height / 2, height, containerHeight);

  return (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute z-[900]"
        style={{
          left: toLeft ? state.x - CONNECTOR : state.x,
          top: state.y - 1,
          width: CONNECTOR + RADIUS,
          marginLeft: toLeft ? -RADIUS : 0,
          height: 2,
          background: BG,
        }}
      />

      <div
        className="absolute z-[900] overflow-hidden rounded-lg shadow-lg"
        style={{ left, top, width, height, background: BG }}
      >
        <ul
          className="h-full overflow-y-auto overscroll-contain py-1.5"
          // 파랑 바탕이라 공통 회색 막대 대신 흰 막대를 쓴다.
          style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(255 255 255 / 0.7) transparent" }}
        >
          {state.sites.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onSitePick(s.id)}
                className="flex h-[1.75rem] w-full items-center gap-2 px-2 text-left hover:bg-black/10"
              >
                <span
                  aria-hidden="true"
                  className="h-2 w-2 flex-none rounded-full ring-1 ring-white/70"
                  style={{ background: severityTextColor(s.severity) }}
                />
                <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-semibold text-white">
                  {s.label}
                </span>
                <span className="flex-none text-[0.625rem] font-medium text-white/80">
                  {SEVERITY_LABELS[s.severity]}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
